/**
 * Sandbox Group API service.
 * Provides CRUD operations for sandbox groups.
 *
 * API Endpoints:
 * - GET /sandboxGroups - List all sandbox groups
 * - GET /sandboxGroups/:id - Get sandbox group by ID
 * - PUT /sandboxGroups - Create sandbox group
 * - DELETE /sandboxGroups/:id - Delete sandbox group
 * - GET /sandboxGroups/:id/sandboxes - List sandboxes in a group
 *
 * @module services/sandboxGroupService
 */

import { httpClient } from './httpClient';
import type {
  SandboxGroupRequest,
  SandboxGroupResponse,
  SandboxResponse,
  DiskImageResponse,
  SnapshotResponse,
  SecretResponse,
  SecretListResponse,
  SecretKeysResponse,
  VolumeResponse,
} from '@/types';

const ENDPOINT = '/sandboxGroups';

/**
 * Sandbox Group Service
 * Encapsulates all sandbox group API operations
 */
export const sandboxGroupService = {
  /**
   * List sandbox groups with pagination
   * @param page - Page number (1-based, default: 1)
   * @param pageSize - Number of items per page (default: 25, max: 100)
   * @returns Promise<SandboxGroupResponse[]> - Array of sandbox groups
   */
  async list(page: number = 1, pageSize: number = 25): Promise<SandboxGroupResponse[]> {
    console.log(
      '[SandboxGroupService] Fetching sandbox groups - Page:',
      page,
      'PageSize:',
      pageSize
    );
    try {
      const response = await httpClient.get<SandboxGroupResponse[]>(
        `${ENDPOINT}?page=${page}&pageSize=${pageSize}`
      );
      console.log('[SandboxGroupService] Fetched', response.data.length, 'sandbox groups');
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch sandbox groups:', error);
      throw error;
    }
  },

  /**
   * Get a sandbox group by ID
   * @param id - The sandbox group ID
   * @returns Promise<SandboxGroupResponse> - The sandbox group
   */
  async getById(id: string): Promise<SandboxGroupResponse> {
    console.log('[SandboxGroupService] Fetching sandbox group:', id);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.get<SandboxGroupResponse>(`${ENDPOINT}/${encodedId}`);
      console.log('[SandboxGroupService] Fetched sandbox group:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch sandbox group:', error);
      throw error;
    }
  },

  /**
   * Create a new sandbox group
   * @param request - The sandbox group creation request
   * @returns Promise<SandboxGroupResponse> - The created sandbox group
   */
  async create(request: SandboxGroupRequest): Promise<SandboxGroupResponse> {
    console.log('[SandboxGroupService] Creating sandbox group:', request);
    try {
      const response = await httpClient.put<SandboxGroupResponse>(ENDPOINT, request);
      console.log('[SandboxGroupService] Created sandbox group:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to create sandbox group:', error);
      throw error;
    }
  },

  /**
   * Delete a sandbox group by ID
   * @param id - The sandbox group ID to delete
   */
  async delete(id: string): Promise<void> {
    console.log('[SandboxGroupService] Deleting sandbox group:', id);
    const encodedId = encodeURIComponent(id);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedId}`);
      console.log('[SandboxGroupService] Deleted sandbox group:', id);
    } catch (error) {
      console.error('[SandboxGroupService] Failed to delete sandbox group:', error);
      throw error;
    }
  },

  /**
   * Update an existing sandbox group
   * @param id - The sandbox group ID to update
   * @param request - The sandbox group update request
   * @returns Promise<SandboxGroupResponse> - The updated sandbox group
   */
  async update(id: string, request: SandboxGroupRequest): Promise<SandboxGroupResponse> {
    console.log('[SandboxGroupService] Updating sandbox group:', id, request);
    const encodedId = encodeURIComponent(id);
    try {
      const response = await httpClient.put<SandboxGroupResponse>(
        `${ENDPOINT}/${encodedId}`,
        request
      );
      console.log('[SandboxGroupService] Updated sandbox group:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to update sandbox group:', error);
      throw error;
    }
  },

  /**
   * List sandboxes in a sandbox group
   * @param groupId - The sandbox group ID
   * @param page - Page number (1-based, default: 1)
   * @param pageSize - Number of items per page (default: 25, max: 100)
   * @returns Promise<SandboxResponse[]> - Array of sandboxes in the group
   */
  async listSandboxes(
    groupId: string,
    page: number = 1,
    pageSize: number = 25
  ): Promise<SandboxResponse[]> {
    console.log(
      '[SandboxGroupService] Fetching sandboxes for group:',
      groupId,
      'Page:',
      page,
      'PageSize:',
      pageSize
    );
    const encodedId = encodeURIComponent(groupId);
    try {
      const response = await httpClient.get<SandboxResponse[]>(
        `${ENDPOINT}/${encodedId}/sandboxes?page=${page}&pageSize=${pageSize}`
      );
      console.log('[SandboxGroupService] Fetched', response.data.length, 'sandboxes for group');
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch sandboxes for group:', error);
      throw error;
    }
  },

  /**
   * List disk images scoped to a sandbox group
   * @param groupId - The sandbox group ID
   * @returns Promise<DiskImageResponse[]> - Array of disk images in the group
   */
  async listDiskImages(groupId: string): Promise<DiskImageResponse[]> {
    console.log('[SandboxGroupService] Fetching disk images for group:', groupId);
    const encodedId = encodeURIComponent(groupId);
    try {
      const response = await httpClient.get<DiskImageResponse[]>(
        `${ENDPOINT}/${encodedId}/diskimages`
      );
      console.log('[SandboxGroupService] Fetched', response.data.length, 'disk images for group');
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch disk images for group:', error);
      throw error;
    }
  },

  /**
   * List snapshots scoped to a sandbox group
   * @param groupId - The sandbox group ID
   * @returns Promise<SnapshotResponse[]> - Array of snapshots in the group
   */
  async listSnapshots(groupId: string): Promise<SnapshotResponse[]> {
    console.log('[SandboxGroupService] Fetching snapshots for group:', groupId);
    const encodedId = encodeURIComponent(groupId);
    try {
      const response = await httpClient.get<SnapshotResponse[]>(
        `${ENDPOINT}/${encodedId}/snapshots`
      );
      console.log('[SandboxGroupService] Fetched', response.data.length, 'snapshots for group');
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch snapshots for group:', error);
      throw error;
    }
  },

  /**
   * Get the count of snapshots scoped to a sandbox group
   * @param groupId - The sandbox group ID
   * @returns Promise<number> - Count of snapshots
   */
  async getSnapshotsCount(groupId: string): Promise<number> {
    const encodedId = encodeURIComponent(groupId);
    try {
      const response = await httpClient.get<number>(`${ENDPOINT}/${encodedId}/snapshots/count`);
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch snapshots count:', error);
      throw error;
    }
  },

  /**
   * Delete a disk image scoped to a sandbox group
   */
  async deleteDiskImage(groupId: string, diskImageId: string): Promise<void> {
    const encodedGroupId = encodeURIComponent(groupId);
    const encodedImageId = encodeURIComponent(diskImageId);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedGroupId}/diskimages/${encodedImageId}`);
    } catch (error) {
      console.error('[SandboxGroupService] Failed to delete disk image:', error);
      throw error;
    }
  },

  /**
   * Delete a snapshot scoped to a sandbox group
   */
  async deleteSnapshot(groupId: string, snapshotId: string): Promise<void> {
    const encodedGroupId = encodeURIComponent(groupId);
    const encodedSnapshotId = encodeURIComponent(snapshotId);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedGroupId}/snapshots/${encodedSnapshotId}`);
    } catch (error) {
      console.error('[SandboxGroupService] Failed to delete snapshot:', error);
      throw error;
    }
  },

  /**
   * List secrets scoped to a sandbox group
   * @param groupId - The sandbox group ID
   * @returns Promise<SecretResponse[]> - Array of secrets in the group
   */
  async listSecrets(groupId: string): Promise<SecretResponse[]> {
    console.log('[SandboxGroupService] Fetching secrets for group:', groupId);
    const encodedId = encodeURIComponent(groupId);
    try {
      const response = await httpClient.get<SecretListResponse>(`${ENDPOINT}/${encodedId}/secrets`);
      console.log(
        '[SandboxGroupService] Fetched',
        response.data.secrets.length,
        'secrets for group'
      );
      return response.data.secrets;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch secrets for group:', error);
      throw error;
    }
  },

  /**
   * Upsert a secret scoped to a sandbox group
   */
  async upsertSecret(
    groupId: string,
    secretId: string,
    values: Record<string, string>
  ): Promise<SecretResponse> {
    console.log('[SandboxGroupService] Upserting secret in group:', groupId, secretId);
    const encodedGroupId = encodeURIComponent(groupId);
    const encodedSecretId = encodeURIComponent(secretId);
    try {
      const response = await httpClient.put<SecretResponse>(
        `${ENDPOINT}/${encodedGroupId}/secrets/${encodedSecretId}`,
        { values }
      );
      console.log('[SandboxGroupService] Upserted secret in group:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to upsert secret in group:', error);
      throw error;
    }
  },

  /**
   * List volumes scoped to a sandbox group
   * @param groupId - The sandbox group ID
   * @returns Promise<VolumeResponse[]> - Array of volumes
   */
  async listVolumes(groupId: string): Promise<VolumeResponse[]> {
    console.log('[SandboxGroupService] Fetching volumes for group:', groupId);
    const encodedId = encodeURIComponent(groupId);
    try {
      const response = await httpClient.get<VolumeResponse[]>(`${ENDPOINT}/${encodedId}/volumes`);
      console.log('[SandboxGroupService] Fetched', response.data.length, 'volumes for group');
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch volumes for group:', error);
      throw error;
    }
  },

  /**
   * Delete a secret scoped to a sandbox group
   */
  async deleteSecret(groupId: string, secretId: string): Promise<void> {
    console.log('[SandboxGroupService] Deleting secret in group:', groupId, secretId);
    const encodedGroupId = encodeURIComponent(groupId);
    const encodedSecretId = encodeURIComponent(secretId);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedGroupId}/secrets/${encodedSecretId}`);
      console.log('[SandboxGroupService] Deleted secret in group:', secretId);
    } catch (error) {
      console.error('[SandboxGroupService] Failed to delete secret in group:', error);
      throw error;
    }
  },

  /**
   * List key names for a group-scoped secret (values are never transferred).
   * Uses GET /sandboxGroups/{groupId}/secrets/{secretId}/keys.
   */
  async listSecretKeys(groupId: string, secretId: string): Promise<string[]> {
    const encodedGroupId = encodeURIComponent(groupId);
    const encodedSecretId = encodeURIComponent(secretId);
    try {
      const response = await httpClient.get<SecretKeysResponse>(
        `${ENDPOINT}/${encodedGroupId}/secrets/${encodedSecretId}/keys`
      );
      return response.data.keys;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch secret keys:', error);
      throw error;
    }
  },

  /**
   * Get a single volume by name scoped to a sandbox group
   */
  async getVolume(groupId: string, volumeName: string): Promise<VolumeResponse> {
    const encodedGroupId = encodeURIComponent(groupId);
    const encodedName = encodeURIComponent(volumeName);
    try {
      const response = await httpClient.get<VolumeResponse>(
        `${ENDPOINT}/${encodedGroupId}/volumes/${encodedName}`
      );
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fetch volume:', error);
      throw error;
    }
  },

  /**
   * Create a volume scoped to a sandbox group
   */
  async createVolume(
    groupId: string,
    volumeName: string,
    request: { type: string; size?: string; labels?: Record<string, string> }
  ): Promise<VolumeResponse> {
    const encodedGroupId = encodeURIComponent(groupId);
    const encodedName = encodeURIComponent(volumeName);
    try {
      const response = await httpClient.put<VolumeResponse>(
        `${ENDPOINT}/${encodedGroupId}/volumes/${encodedName}`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to create volume:', error);
      throw error;
    }
  },

  /**
   * Delete a volume scoped to a sandbox group
   * @param groupId - The sandbox group ID
   * @param volumeName - The name of the volume to delete
   */
  async deleteVolume(groupId: string, volumeName: string): Promise<void> {
    const encodedGroupId = encodeURIComponent(groupId);
    const encodedName = encodeURIComponent(volumeName);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedGroupId}/volumes/${encodedName}`);
    } catch (error) {
      console.error('[SandboxGroupService] Failed to delete volume:', error);
      throw error;
    }
  },

  async forkVolume(
    groupId: string,
    sourceVolumeName: string,
    request: { destinationVolumeName: string; labels?: Record<string, string> }
  ): Promise<VolumeResponse> {
    const encodedGroupId = encodeURIComponent(groupId);
    const encodedName = encodeURIComponent(sourceVolumeName);
    try {
      const response = await httpClient.post<VolumeResponse>(
        `${ENDPOINT}/${encodedGroupId}/volumes/${encodedName}/fork`,
        request
      );
      return response.data;
    } catch (error) {
      console.error('[SandboxGroupService] Failed to fork volume:', error);
      throw error;
    }
  },
};
